import * as React from 'react';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';
import { AppState } from 'src';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { AccountViewModel } from '@models/Account';
import { accountService } from '@services/AccountService';
import { NotFoundError } from '@services/BaseService';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum CreditCardsActionType {
  DeleteAccountFailure = 'DeleteAccountFailure',
  DeleteAccountStart = 'DeleteAccountStart',
  DeleteAccountSuccess = 'DeleteAccountSuccess',
  GetAccountFailure = 'GetAccountFailure',
  GetAccountStart = 'GetAccountStart',
  GetAccountSuccess = 'GetAccountSuccess',
  ResetCreditCard = 'ResetCreditCard'
}

interface CreditCardsAction extends Action {
  readonly type: CreditCardsActionType;
}

export interface DeleteAccountFailureAction extends CreditCardsAction {
  readonly errorMessage: string;
}
export interface DeleteAccountStartAction extends CreditCardsAction {}
export interface DeleteAccountSuccessAction extends CreditCardsAction {}

export interface GetAccountFailureAction extends CreditCardsAction {
  readonly errorMessage: string;
}
export interface GetAccountStartAction extends CreditCardsAction {}
export interface GetAccountSuccessAction extends CreditCardsAction {
  readonly account?: AccountViewModel;
}

export interface UpdateAccountFailureAction extends CreditCardsAction {
  readonly isCreditCardInvalid: boolean;
  readonly isError: boolean;
}
export interface UpdateAccountStartAction extends CreditCardsAction {}
export interface UpdateAccountSuccessAction extends CreditCardsAction {
  readonly account: AccountViewModel;
}

export interface ResetCreditCardAction extends CreditCardsAction {
  readonly type: CreditCardsActionType.ResetCreditCard;
}

export const deleteAccount = (accountId: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(deleteAccountStart());
    try {
      await accountService.deleteAccount(accountId);
      return dispatch(deleteAccountSuccess());
    } catch (e) {
      return dispatch(deleteAccountFailure(e.toString()));
    }
  };
};

export function deleteAccountFailure(errorMessage: string): DeleteAccountFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);
  return {
    type: CreditCardsActionType.DeleteAccountFailure,
    errorMessage: errorMessage
  };
}

export function deleteAccountStart(): DeleteAccountStartAction {
  return {
    type: CreditCardsActionType.DeleteAccountStart
  };
}

export function deleteAccountSuccess(): DeleteAccountSuccessAction {
  return {
    type: CreditCardsActionType.DeleteAccountSuccess
  };
}

export const getAccount = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getAccountStart());
    try {
      const account = await accountService.getAccount(id);
      return dispatch(getAccountSuccess(account));
    } catch (e) {
      if (e instanceof NotFoundError) {
        return dispatch(getAccountSuccess());
      }
      return dispatch(getAccountFailure(e.toString()));
    }
  };
};

export function getAccountFailure(error: string): GetAccountFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={`Unable to get credit card. Please try again. ${error}`} />,
    ToastOptions.Error
  );
  return {
    type: CreditCardsActionType.GetAccountFailure,
    errorMessage: error.toString()
  };
}

export function getAccountStart(): GetAccountStartAction {
  return {
    type: CreditCardsActionType.GetAccountStart
  };
}

export function getAccountSuccess(account?: AccountViewModel): GetAccountSuccessAction {
  return {
    type: CreditCardsActionType.GetAccountSuccess,
    account: account
  };
}

export function resetCreditCardList() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(resetCreditCard());
  };
}

export function resetCreditCard(): ResetCreditCardAction {
  return {
    type: CreditCardsActionType.ResetCreditCard
  };
}



// WEBPACK FOOTER //
// ./src/common/components/credit-cards/credit-card.actions.tsx